import { useParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import PriceCalculator from "@/components/PriceCalculator";
import ReviewsSection from "@/components/ReviewsSection";
import TrustBadges from "@/components/TrustBadges";
import { Card } from "@/components/ui/card";
import { CheckCircle2, MapPin } from "lucide-react";

const CITY_DATA = {
  noida: {
    name: "Noida",
    hero: "Packers and Movers in Noida — Sector-Wise Local Experts",
    areas: ["Sector 62", "Sector 78", "Sector 137", "Sector 150", "Sector 18", "Noida Expressway", "Sec 76", "Sec 44"],
    pricing: [["1 BHK Local", "₹6,000 – ₹9,000"], ["2 BHK Local", "₹11,000 – ₹16,000"], ["3 BHK Local", "₹16,000 – ₹24,000"], ["Noida → Gurugram", "₹18,000 – ₹28,000"], ["Noida → Ghaziabad", "₹8,000 – ₹14,000"]],
    highlight: "Noida ke har sector mein 90 min mein team pahunchti hai. Society NOC, elevator booking — hum handle karte hain.",
  },
  "greater-noida": {
    name: "Greater Noida",
    hero: "Packers and Movers in Greater Noida — Pari Chowk to Alpha",
    areas: ["Pari Chowk", "Alpha 1 & 2", "Beta 1", "Omicron", "Sigma", "Techzone", "Knowledge Park", "Jaypee Greens"],
    pricing: [["1 BHK Local", "₹7,000 – ₹10,000"], ["2 BHK Local", "₹12,000 – ₹18,000"], ["3 BHK Local", "₹18,000 – ₹26,000"], ["Gr Noida → Noida", "₹9,000 – ₹15,000"], ["Gr Noida → Delhi", "₹15,000 – ₹22,000"]],
    highlight: "Jewar airport corridor ke saare sectors covered. Bade society campuses ka permission hum lete hain.",
  },
  ghaziabad: {
    name: "Ghaziabad",
    hero: "Packers and Movers in Ghaziabad — Indirapuram & Beyond",
    areas: ["Indirapuram", "Vaishali", "Vasundhara", "Kaushambi", "Raj Nagar", "Crossings Republik", "Nyay Khand", "Ahinsa Khand"],
    pricing: [["1 BHK Local", "₹6,000 – ₹9,500"], ["2 BHK Local", "₹11,500 – ₹17,000"], ["3 BHK Local", "₹17,000 – ₹25,000"], ["Ghaziabad → Delhi", "₹10,000 – ₹18,000"], ["Ghaziabad → Noida", "₹8,000 – ₹14,000"]],
    highlight: "NH-24 aur Meerut Road corridor ke ander aur intercity dono handle karte hain. Ghaziabad mein hamara main warehouse hai.",
  },
  gurugram: {
    name: "Gurugram",
    hero: "Packers and Movers in Gurugram — DLF to Sohna Road",
    areas: ["DLF Phase 1-5", "Cyber City", "Sohna Road", "Sec 56", "Sec 49", "Golf Course Road", "MG Road", "Manesar"],
    pricing: [["1 BHK Local", "₹7,000 – ₹11,000"], ["2 BHK Local", "₹13,000 – ₹19,000"], ["3 BHK Local", "₹19,000 – ₹28,000"], ["Gurgaon → Noida", "₹18,000 – ₹28,000"], ["Gurgaon → Delhi", "₹10,000 – ₹16,000"]],
    highlight: "High-rise society ke building rules, tower crane bookings, corporate office shifts — hum specialist hain.",
  },
  faridabad: {
    name: "Faridabad",
    hero: "Packers and Movers in Faridabad — NIT to Neharpar",
    areas: ["Sec 21", "Sec 15", "Sec 88", "NIT", "Neharpar", "Old Faridabad", "Ballabgarh", "Greater Faridabad"],
    pricing: [["1 BHK Local", "₹6,500 – ₹9,500"], ["2 BHK Local", "₹12,000 – ₹17,500"], ["3 BHK Local", "₹17,500 – ₹25,500"], ["Faridabad → Delhi", "₹9,000 – ₹15,000"], ["Faridabad → Gurgaon", "₹14,000 – ₹22,000"]],
    highlight: "Faridabad se Delhi border cross karne mein experience — kum se kum documentation, no delay.",
  },
  delhi: {
    name: "Delhi",
    hero: "Packers and Movers in Delhi — All Areas Covered",
    areas: ["Dwarka", "Rohini", "South Ex", "Saket", "Karol Bagh", "Lajpat Nagar", "Vasant Kunj", "Pitampura"],
    pricing: [["1 BHK Local", "₹6,000 – ₹9,000"], ["2 BHK Local", "₹11,000 – ₹16,000"], ["3 BHK Local", "₹16,000 – ₹24,000"], ["Delhi → Noida", "₹8,000 – ₹14,000"], ["Delhi → Gurgaon", "₹10,000 – ₹16,000"]],
    highlight: "Old Delhi ki narrow lanes ho ya South Delhi ke bungalows — hamari team har jagah trained hai.",
  },
};

export default function CityPage() {
  const { city } = useParams();
  const key = city.replace("packers-movers-", "");
  const data = CITY_DATA[key] || CITY_DATA.noida;

  return (
    <PublicLayout>
      <section className="bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-[1fr_auto] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-orange-500/20 rounded-full px-3 py-1 text-xs font-semibold text-orange-200 mb-3">
              <MapPin className="w-3 h-3" /> Serving {data.name} since 2010
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight" data-testid="city-hero-heading">{data.hero}</h1>
            <p className="mt-4 text-slate-200 max-w-2xl">{data.highlight}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {data.areas.map((a) => (
                <span key={a} className="bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs">{a}</span>
              ))}
            </div>
          </div>
          <PriceCalculator />
        </div>
      </section>

      <TrustBadges />

      <section className="py-14 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0F172A] mb-6">Approx {data.name} Shifting Prices</h2>
        <Card className="overflow-hidden">
          <table className="w-full text-left" data-testid="city-pricing-table">
            <thead className="bg-slate-100">
              <tr>
                <th className="p-4 font-semibold text-[#0F172A]">Shifting Type</th>
                <th className="p-4 font-semibold text-[#0F172A]">Price Range</th>
              </tr>
            </thead>
            <tbody>
              {data.pricing.map(([k, v]) => (
                <tr key={k} className="border-t">
                  <td className="p-4 text-slate-700">{k}</td>
                  <td className="p-4 font-semibold text-[#F97316]">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <p className="text-xs text-slate-500 mt-3">*Final quote depends on floor, elevator access, packing quality. Free home survey available.</p>
      </section>

      <section className="py-14 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0F172A]">What's Included in {data.name} Shifting?</h2>
          <div className="grid md:grid-cols-2 gap-4 mt-6">
            {[
              "Free pre-move home survey & fixed quotation",
              "5-layer professional packing material (bubble wrap, carton, stretch film)",
              "Trained loading & unloading team",
              "Own GPS-tracked fleet — no sub-contracting",
              "Transit insurance up to ₹5,00,000",
              "Furniture dismantling & re-assembly",
              "Unpacking assistance at destination",
              "24×7 owner-handled WhatsApp support",
            ].map((f) => (
              <div key={f} className="flex gap-3 bg-white p-4 rounded-lg border border-slate-200">
                <CheckCircle2 className="w-5 h-5 text-[#22C55E] flex-none" />
                <span className="text-slate-700 text-sm">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ReviewsSection />
    </PublicLayout>
  );
}
